import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

// Design-system Badge atom (resend.md components.badge / components.statusChip):
// JetBrains Mono, uppercase, 10px, 700 weight, 0.04em tracking, 2px 8px padding,
// bordered. The square (0-radius) regime is baked in via the always-emitted
// data-slot="badge" hook, which matches the global rule in src/index.css —
// callers no longer supply it, and no per-screen re-skin is needed.
const badgeVariants = cva(
	'inline-flex items-center border px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-[0.04em] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
	{
		variants: {
			variant: {
				default:
					'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
				secondary:
					'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
				destructive:
					'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
				outline: 'text-foreground',
			},
			// Status chip colors — ~15% background tint + full-opacity text, drawn
			// from the functional status tokens in src/index.css. Covers the five
			// colors the delivery-log / settings / retry-processor screens use.
			status: {
				success: 'border-success/40 bg-success/15 text-success',
				danger: 'border-danger/40 bg-danger/15 text-danger',
				warning: 'border-warning/40 bg-warning/15 text-warning',
				info: 'border-info/40 bg-info/15 text-info',
				neutral: 'border-border bg-muted text-muted-foreground',
			},
		},
	},
);

/** The non-null status variant keys, for per-screen domain-enum → status maps. */
export type BadgeStatus = NonNullable<VariantProps<typeof badgeVariants>['status']>;

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, status, ...props }: BadgeProps) {
	return (
		<div
			data-slot="badge"
			className={cn(badgeVariants({ variant, status }), className)}
			{...props}
		/>
	);
}

export { Badge, badgeVariants };
